/**
 * ABM Provincias — Fase 8
 *
 * Catálogo geográfico simple (solo nombre). Hard delete con restricción
 * por FK desde localidades (Postgres bloquea automáticamente).
 */

import { useState, useMemo } from 'react';
import {
  Box,
  Card,
  Typography,
  Button,
  Stack,
  IconButton,
  Tooltip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
} from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridRenderCellParams,
} from '@mui/x-data-grid';
import { Add, Edit, Delete } from '@mui/icons-material';

import {
  useProvincias,
  useCreateProvincia,
  useUpdateProvincia,
  useDeleteProvincia,
  type Provincia,
} from '../../../hooks/useCatalogos';

export default function ProvinciasList() {
  const { data: provincias = [], isLoading, isError, error } = useProvincias();
  const create = useCreateProvincia();
  const update = useUpdateProvincia();
  const del = useDeleteProvincia();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Provincia | null>(null);
  const [nombre, setNombre] = useState('');
  const [nombreError, setNombreError] = useState<string | null>(null);

  const openCreate = () => {
    setEditing(null);
    setNombre('');
    setNombreError(null);
    setDialogOpen(true);
  };

  const openEdit = (p: Provincia) => {
    setEditing(p);
    setNombre(p.nombre);
    setNombreError(null);
    setDialogOpen(true);
  };

  const handleDelete = async (p: Provincia) => {
    if (!confirm(`¿Eliminar la provincia "${p.nombre}"? Esto puede fallar si hay localidades o empresas asociadas.`)) return;
    try {
      await del.mutateAsync(p.id);
    } catch (e) {
      alert(`No se pudo eliminar: ${(e as Error).message}`);
    }
  };

  const handleSubmit = async () => {
    const n = nombre.trim();
    if (n.length < 2) {
      setNombreError('El nombre debe tener al menos 2 caracteres');
      return;
    }
    if (n.length > 100) {
      setNombreError('Máximo 100 caracteres');
      return;
    }
    try {
      if (editing) await update.mutateAsync({ id: editing.id, nombre: n });
      else await create.mutateAsync(n);
      setDialogOpen(false);
    } catch {
      // se muestra abajo
    }
  };

  const apiError = (editing ? update.error : create.error) as Error | null;
  const submitting = (editing ? update.isPending : create.isPending) || del.isPending;

  const columns = useMemo<GridColDef<Provincia>[]>(
    () => [
      { field: 'nombre', headerName: 'Nombre', flex: 1, minWidth: 250 },
      {
        field: 'actions',
        headerName: '',
        width: 110,
        sortable: false,
        filterable: false,
        renderCell: (p: GridRenderCellParams<Provincia>) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Editar">
              <IconButton size="small" onClick={() => openEdit(p.row)}>
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Eliminar">
              <IconButton size="small" color="error" onClick={() => handleDelete(p.row)}>
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [],
  );

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Provincias
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Catálogo geográfico (las localidades dependen de provincias).
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
          Nueva provincia
        </Button>
      </Stack>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error al cargar: {(error as Error)?.message ?? 'desconocido'}
        </Alert>
      )}

      <Card>
        <DataGrid
          rows={provincias}
          columns={columns}
          loading={isLoading}
          autoHeight
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          sx={{ border: 0 }}
          localeText={{ noRowsLabel: 'No hay provincias cargadas' }}
        />
      </Card>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editing ? 'Editar provincia' : 'Nueva provincia'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {apiError && <Alert severity="error">{apiError.message}</Alert>}
            <TextField
              autoFocus
              required
              fullWidth
              label="Nombre"
              value={nombre}
              onChange={(e) => {
                setNombre(e.target.value);
                setNombreError(null);
              }}
              error={!!nombreError}
              helperText={nombreError}
              inputProps={{ maxLength: 100 }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {editing ? 'Guardar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
